// Components
import React, { Component } from 'react';
import moment from 'moment';
import { Popover, OverlayTrigger, Tooltip } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import NewUserButton from '../components/NewUserButton';
import DeleteUserButton from '../components/DeleteUserButton';
import ListKeysButton from '../components/ListKeysButton';
import ListUserGroupsButton from '../components/ListUserGroupsButton';
import ManageUserPoliciesButton from '../components/ManageUserPoliciesButton';
import LoadingSpinner from '../components/LoadingSpinner';

// External
import Reactable from 'reactable';

// To handle requests
import UsersAPI from '../utils/UsersAPI';
import MetricsAPI from '../utils/MetricsAPI';
import AccountsAPI from '../utils/AccountsAPI';
import KeysAPI from '../utils/KeysAPI';
import getDateFormat from '../utils/getDateFormat';
import metricsTools from '../utils/metricsTools';

// CSS
import '../components/Panel.css';
import '../pages/ResponsiveTable.css';
import brandingStyles from '../branding/brandingStyles';

// Tools
import copyToClipboard from '../utils/CopyToClipboard';
import compareDates from '../utils/compareDates';
import { namespace } from '../../../config.json';


// Setting reactable values instead of regular table
const Table = Reactable.Table;
const Thead = Reactable.Thead;
const Th = Reactable.Th;
const Tr = Reactable.Tr;
const Td = Reactable.Td;

class UsersComponent extends Component {

    constructor() {
        super();

        const nbItemsPagination = JSON.parse(
          localStorage.getItem('scality-nbitems-users')
        );
        const metricsStartDate
        = metricsTools.momentToStart(moment().subtract(1, 'days'));
        const metricsEndDate = metricsTools.momentToEnd(moment());
        this.state = {
            users: [],
            filterText: '',
            currentIndex: 0,
            maxIndex: -1,
            marker: null,
            nbItems: nbItemsPagination || 20,
            dataLoaded: false,
            accountId: null,
            accountName: null,
            quota: 0,
            diskUsage: 0,
            metricsStartDate,
            metricsEndDate,
            metricsError: false,
        };
        // We need to bind this to functions so we can have
        // the proper this reference inside the method
        this.handleFilterChange = this.handleFilterChange.bind(this);
        this.handlePreviousPage = this.handlePreviousPage.bind(this);
        this.handleNextPage = this.handleNextPage.bind(this);
        this.handleUserAction = this.handleUserAction.bind(this);
        this.getUserRow = this.getUserRow.bind(this);
        this.refresh = this.refresh.bind(this);
        this.mergeUtapiDataInState = this.mergeUtapiDataInState.bind(this);
        this.mounted = false;
        this.metrics = new MetricsAPI();
    }

    getUserRow(user) {
        const actGroup =
        <div className="btn-group btn-group-xs user-actions">
          <ListUserGroupsButton username={user.UserName}/>
          <ManageUserPoliciesButton
            username={user.UserName}
          />
          <ListKeysButton
            username={user.UserName}
            userType="user"
            getKeys={KeysAPI.getKeys}
            deleteKey={KeysAPI.deleteKey}
            createKey={KeysAPI.createKey}/>
          <DeleteUserButton
            username={user.UserName}
            callback={this.handleUserAction}/>
        </div>;

        const arnPopover = (
            <Popover
              id="popover-user-arn-value"
              title="Amazon Resource Name (ARN)"
            >
              <p style={{ wordWrap: 'break-word' }}>
                  {user.Arn}
              </p>
              <hr />
              <strong>
                {this.state.copiedState
                  ? 'Copied' : 'Click to copy to clipboard'}
              </strong>
            </Popover>
        );

        const arnEyeIcon = (
            <OverlayTrigger
                trigger={['hover', 'focus']}
                placement="top"
                overlay={arnPopover}>
                    <i
                      className="fa fa-eye"
                      style={Object.assign(
                          {
                              fontSize: '18px',
                              cursor: 'pointer',
                          },
                        brandingStyles.mainColor
                      )}
                      onClick={this.copyArnToClipboard.bind(this, user.Arn)}
                    />
            </OverlayTrigger>
        );

        return (
            <Tr
              key={user.UserId}
              className="user-row"
              style={brandingStyles.fontColor}
            >
                <Td
                  column="UserName"
                  data={user.UserName}
                  className="bold padding-l-25"
                >
                    {user.UserName}
                </Td>
                <Td column="Arn">
                  {arnEyeIcon}
                </Td>
                <Td column="storageUtilized" value={user.storageUtilized}>
                  {
                      !this.state.metricsError &&
                    (user.storageUtilized !== undefined ?
                    metricsTools.bytesToSize(user.storageUtilized) :
                    <i className="fa fa-refresh fa-spin"/>)
                    || '-'
                  }
                </Td>
                <Td column="UsedBandwidth">
                  {
                      !this.state.metricsError &&
                    (user.incomingBytes !== undefined ?
                    <span>
                      <OverlayTrigger
                        trigger={['hover', 'focus']}
                        placement="top"
                        overlay={
                          <Tooltip id="incomingDataTooltip">
                            Incoming data
                          </Tooltip>
                        }>
                          <span>
                              <i
                                style={brandingStyles.mainColor}
                                className="fa fa-arrow-circle-up"
                              />
                              &nbsp;
                              {metricsTools.bytesToSize(user.incomingBytes)}
                          </span>
                      </OverlayTrigger>
                      &nbsp;
                      <OverlayTrigger
                        trigger={['hover', 'focus']}
                        placement="top"
                        overlay={
                          <Tooltip id="outgoingDataTooltip">
                            Outgoing data
                          </Tooltip>
                        }>
                          <span>
                              <i
                                style={brandingStyles.mainColor}
                                className="fa fa-arrow-circle-down"
                              />
                              &nbsp;
                              {metricsTools.bytesToSize(user.outgoingBytes)}
                          </span>
                      </OverlayTrigger>
                    </span> :
                    <i className="fa fa-refresh fa-spin"/>)
                    || '-'
                  }
                </Td>
                <Td column="CreateDate">
                    {getDateFormat(new Date(user.CreateDate))}
                </Td>
                <Td column="Actions">
                  {actGroup}
                </Td>
            </Tr>
        );
    }

    componentDidMount() {
        this.mounted = true;
        AccountsAPI.getAccountId().then(res => {
            if (this.mounted) {
                this.setState({ accountId: res.accountId, accountName: res.accountName },
                  () => {
                      AccountsAPI.accountGetQuotaForAccount(res.accountId)
                      .then(res => {
                          this.setState({ quota: res.quota });
                      });
                      this.metrics.getAccountMetrics(
                        { accountId: this.state.accountId, accountName: this.state.accountName },
                        this.state.metricsStartDate,
                        this.state.metricsEndDate,
                      )
                      .then(res => {
                          if (res.metrics.storageUtilized && res.metrics.storageUtilized.length) {
                              this.setState({
                                  diskUsage: res.metrics.storageUtilized[1],
                              });
                          }
                      })
                      .catch(res => {
                          if (res.status === 401 || res.status === 403) {
                              window.location.href =
                                `${namespace}/login/error/${res.status}`;
                          }
                          if (this.mounted) {
                              this.setState({
                                  metricsError: true,
                              });
                          }
                      });
                      this.refresh();
                  }
                );
            }
        }).catch(message => {
            if (this.mounted) {
                this.setState({ errorMessage: message.response.body.error });
            }
        });
    }
    componentWillUnmount() {
        this.mounted = false;
    }

    // To handle table filter change
    handleFilterChange(e) {
        if (this.mounted) {
            this.setState({ filterText: e.target.value });
        }
    }

    handlePreviousPage() {
        if (this.mounted) {
            this.setState({
                currentIndex: this.state.currentIndex - 1,
            });
        }
    }

    handleNextPage() {
        const currentIndex = this.state.currentIndex;
        if (this.mounted) {
            this.setState({
                currentIndex: this.state.currentIndex + 1,
            });
        }
        if (currentIndex + 1 === this.state.users.length) {
            this.refresh();
        }
    }

    handleUserAction() {
        if (this.mounted) {
            this.setState({
                users: [],
                filterText: '',
                currentIndex: 0,
                maxIndex: -1,
                marker: null,
            }, this.refresh);
        }
    }

    handleNbItemsSelect(event) {
        localStorage.setItem('scality-nbitems-users',
        JSON.stringify(event.target.value));
        if (this.mounted) {
            this.setState({
                nbItems: event.target.value,
            }, this.handleUserAction);
        }
    }

    copyArnToClipboard(value) {
        const success = copyToClipboard(value);
        this.setCopied(success);
    }

    setCopied(success) {
        if (success && this.mounted) {
            this.setState({ copiedState: true });
            setTimeout(() => {
                this.setState({ copiedState: false });
            }, 2000);
        }
    }

    mergeUtapiDataInState(data, index) {
        // Deep cloning existing users array
        const current = JSON.parse(JSON.stringify(this.state.users[index]));

        if (current.length === data.length) {
            for (let i = 0; i < current.length; i++) {
                current[i].storageUtilized = data[i].storageUtilized[1];
                current[i].incomingBytes = data[i].incomingBytes;
                current[i].outgoingBytes = data[i].outgoingBytes;
            }
        }
        const res = this.state.users;
        res[index] = current;
        if (this.mounted) {
            this.setState({ users: res });
        }
    }

    // Refresh users list
    refresh() {
        UsersAPI.getUsers(this.state.nbItems, this.state.marker).then(res => {
            const userPages = this.state.users;
            userPages.push(res.users);
            if (this.mounted) {
                this.setState({
                    users: userPages,
                    marker: res.marker,
                    maxIndex: res.marker ? -1 : this.state.currentIndex,
                    dataLoaded: true,
                });
            }
        }).catch(message => {
            if (message.status === 401 || message.status === 403) {
                if (message.response.body
                  && message.response.body.error === 'The AWS access key Id ' +
                    'you provided does not exist in our records.') {
                    window.location.href =
                        `${namespace}/login/error/invalidkey`;
                } else {
                    window.location.href =
                        `${namespace}/login/error/${message.status}`;
                }
            } else {
                if (this.mounted) {
                    this.setState({
                        errorMessage: message.response.body.error,
                    });
                }
            }
        }
        );
    }

    render() {
        let quotaAlert = null;
        if (this.state.quota !== 0 && this.state.diskUsage !== 0) {
            if (this.state.quota <= this.state.diskUsage) {
                quotaAlert = <div className="alert alert-warning">
                  <strong>Warning : </strong>
                  You reached your disk usage quota. Please contact your
                  administrator if you need to increase your quota.
                </div>;
            }
        }
        let errorAlert;
        if (this.state.errorMessage) {
            errorAlert = (
              <div className="alert alert-warning">
                <strong>Error : </strong>
                {this.state.errorMessage}
              </div>
            );
        }

        let usersList;
        const currentUsers = this.state.users[this.state.currentIndex];
        if (currentUsers) {
            // Map over the contacts and get an element for each of them
            usersList = currentUsers.map(
              user => this.getUserRow(user)
            );
        }

        const arnPopover = (
            <Popover id="popover-user-arn" title="Amazon Resource Name (ARN)">
                <p>
                    {'Each IAM user has a unique ARN with the format: '}
                </p>
                <p>
                    {'arn:<partition>:<service>::<account-id>:'}
                    {'<resourcetype/resource>'}
                </p>
            </Popover>
        );

        const arnHelpIcon = (
            <OverlayTrigger
                trigger={['hover', 'focus']}
                placement="right"
                overlay={arnPopover}>
                    <i className="fa fa-question-circle-o"></i>
            </OverlayTrigger>
        );

        const table = <Table
          className="table table-striped"
          id="table"
          sortable={[
              'UserName',
              'storageUtilized',
              {
                  column: 'CreateDate',
                  sortFunction: compareDates,
              },
          ]}
          filterBy={this.state.filterText}
          hideFilterInput
          filterable={['UserName', 'Arn']}>
            <Thead>
                <Th column="UserName" className="padding-l-25">
                    <strong className="name-header">Username</strong>
                </Th>
                <Th column="Arn">
                    <strong className="name-header">ARN</strong>
                    {' '}{arnHelpIcon}
                </Th>
                <Th column="CreateDate">
                    <strong className="name-header">Created on</strong>
                </Th>
                <Th column="Actions">
                    <strong className="name-header">Actions</strong>
                </Th>
            </Thead>
            {usersList}
        </Table>;

        const prevDisabled = this.state.currentIndex === 0;
        const nextDisabled = this.state.currentIndex === this.state.maxIndex;

        const pagerInstance = (
          <ul className="pager padding-l-15 padding-r-15">
            <li
              className={prevDisabled ? 'previous disabled' : 'previous'}
            >
              <a
                className="clickable"
                onClick={this.handlePreviousPage}
                style={brandingStyles.buttons.primary}
              >&larr;</a>
            </li>
            <li
              className={nextDisabled ? 'next disabled' : 'next'}
            >
              <a
                className="clickable"
                onClick={this.handleNextPage}
                style={brandingStyles.buttons.primary}
              >&rarr;</a>
            </li>
          </ul>
        );

        return (
            <div id="page-wrapper" style={brandingStyles.backgrounds.main}>
                <PageHeader facode="user-circle-o" title="Users"/>
                {quotaAlert}
                {errorAlert}
                <div
                  className="panel panel-default"
                  style={brandingStyles.backgrounds.panel}
                >
                    <div className="panel-body padding-0">
                        <div className="padding-15">
                            <div style={{
                                display: 'inline-block',
                            }}>
                              <NewUserButton
                              callback={this.handleUserAction}/>
                            </div>
                              <select
                              className="form-control pull-right
                              pagination-select"
                              style={brandingStyles.backgrounds.input}
                              value={this.state.nbItems}
                              onChange={this.handleNbItemsSelect.bind(this)}>
                                <option value="20">20</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                              </select>
                            </div>
                            <input
                              className="form-control full-width"
                              style={brandingStyles.backgrounds.input}
                              placeholder="Find users"
                              value={this.state.filterText}
                              onChange={this.handleFilterChange}
                            >
                            </input>
                        <div className="table-responsive">
                          {this.state.dataLoaded ? table : <LoadingSpinner />}
                        </div>
                        {pagerInstance}
                    </div>
                </div>
            </div>
        );
    }
}

export default UsersComponent;



/** WEBPACK FOOTER **
 ** ./src/pages/Users.js
 **/